import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { Button, CircularProgress, Grid, Stack } from '@mui/material'
import { updateCarePerson } from 'api/carePersons'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID } from 'const'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import moment from 'moment'
import React from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { BackendGender, CarePerson } from 'types'
import { TypeOf, z } from 'zod'
import FormDatePicker from '../../../form-elements/FormDatePicker'
import FormInput from '../../../form-elements/FormInput'
import FormRadioGroup from '../../../form-elements/FormRadioGroup'
import FormSelect from '../../../form-elements/FormSelect'
import { careLevels } from 'models/user.model'

const genderOptions = {
  male: BackendGender.male,
  female: BackendGender.female,
  'Prefer to self describe': BackendGender.selfDescribe,
  'Prefer not to answer': BackendGender.notAnswer,
}

const yesNoOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
]

const WHAT_MATTERS_HELPER =
  'How does the person of concern answer the questions ‘What brings you joy? What makes life worth living?’'
const WHAT_MATTERS_MAX_LENGTH = 50

type Props = {
  carePerson: CarePerson
  readonly?: boolean
  onPersonUpdated: (person: CarePerson) => void
}

const required = z
  .string({
    required_error: 'Required',
    invalid_type_error: 'Invalid value',
  })
  .min(1, 'Required')

const formSchema = z.object({
  email: z.string().email(EMAIL_IS_INVALID).optional().or(z.literal('')),
  firstname: required,
  lastname: z.string().optional(),
  streetAddress: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  phoneNumber: z.string().optional(),
  dateOfBirth: z
    .date({
      invalid_type_error: 'Invalid date',
      coerce: true,
    })
    .optional(),
  gender: z.string().optional(),
  careLevel: z.string().optional(),
  advancedDirective: z.string().optional(),
  polstForm: z.string().optional(),
  whatMatters: z.string().max(WHAT_MATTERS_MAX_LENGTH).optional(),
})

type FormType = TypeOf<typeof formSchema>

const formSettings = {
  mode: 'onChange' as const,
  resolver: zodResolver(formSchema),
}

const ConcernProfileBasicInfoForm = ({ carePerson, readonly, onPersonUpdated }: Props) => {
  const queryClient = useQueryClient()
  const formMethods = useForm<FormType>({
    ...formSettings,
    defaultValues: {
      city: carePerson.city ?? '',
      country: carePerson.country ?? '',
      email: carePerson.email ?? '',
      firstname: carePerson.firstname ?? '',
      gender: carePerson.gender ?? '',
      careLevel: carePerson.careLevel ?? '',
      lastname: carePerson.lastname ?? '',
      phoneNumber: carePerson.phoneNumber ?? '',
      state: carePerson.state ?? '',
      streetAddress: carePerson.streetAddress ?? '',
      zipCode: carePerson.zipCode ?? '',
      dateOfBirth: carePerson.dateOfBirth ? moment(carePerson.dateOfBirth).toDate() : undefined,
      advancedDirective: carePerson.advancedDirective ?? '',
      polstForm: carePerson.polstForm ?? '',
      whatMatters: carePerson.whatMatters ?? '',
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = (values: FormType) => {
    updater.mutate(
      {
        city: values.city,
        country: values.country,
        dateOfBirth: values.dateOfBirth
          ? moment(values.dateOfBirth).format('YYYY-MM-DD')
          : undefined,
        email: values.email,
        firstname: values.firstname,
        gender: values.gender || undefined,
        careLevel: values.careLevel || undefined,
        streetAddress: values.streetAddress,
        zipCode: values.zipCode,
        lastname: values.lastname,
        phoneNumber: values.phoneNumber,
        state: values.state,
        advancedDirective: values.advancedDirective || undefined,
        polstForm: values.polstForm || undefined,
        whatMatters: values.whatMatters || undefined,
      },
      {
        onSuccess: ({ data }) => {
          onPersonUpdated(data)
          void queryClient.invalidateQueries('getTeams')
        },
      },
    )
  }

  const onError = () => {
    NotificationSys.showWarning(
      'Profile not saved. Please complete the required fields before saving',
    )
  }

  const updater = useMutation((values: Partial<CarePerson>) =>
    updateCarePerson(carePerson.id, values),
  )

  const whatMattersValue = formMethods.watch('whatMatters')
  const whatMattersCharsLeft = useCharactersLeft(
    WHAT_MATTERS_MAX_LENGTH,
    whatMattersValue?.length || 0,
  )

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(onSubmitHandler, onError)}
        noValidate
        autoComplete="off"
      >
        <FormInput
          disabled={readonly}
          label="Email Address"
          fullWidth
          sx={{ mb: 1 }}
          name="email"
          autoComplete="new-password"
        />

        <Grid container columnSpacing={2}>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="First Name"
              required
              fullWidth
              name="firstname"
              sx={{ mb: 1 }}
              autoComplete="new-password"
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="Last Name"
              fullWidth
              name="lastname"
              sx={{ mb: 1 }}
              autoComplete="new-password"
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="Street Address"
              fullWidth
              name="streetAddress"
              sx={{ mb: 1 }}
              autoComplete="new-password"
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="Zip Code"
              fullWidth
              name="zipCode"
              sx={{ mb: 1 }}
              autoComplete="new-password"
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="City"
              fullWidth
              name="city"
              sx={{ mb: 1 }}
              autoComplete="new-password"
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="State"
              fullWidth
              name="state"
              sx={{ mb: 1 }}
              autoComplete="new-password"
            />
          </Grid>
        </Grid>

        <Grid container columnSpacing={2} sx={{ mb: 1 }}>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="Country"
              fullWidth
              name="country"
              autoComplete="new-password"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={readonly}
              label="Mobile Phone Number"
              fullWidth
              name="phoneNumber"
              autoComplete="new-password"
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormDatePicker
              disabled={readonly}
              label="Date of Birth"
              name="dateOfBirth"
              fullWidth
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormRadioGroup
              disabled={readonly}
              label="Sex"
              required={false}
              name="gender"
              options={Object.entries(genderOptions).map((e) => ({ label: e[0], value: e[1] }))}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormSelect
              disabled={readonly}
              name="careLevel"
              label="Level of Required Care"
              options={careLevels}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <Stack spacing={1}>
              <FormRadioGroup
                disabled={readonly}
                label="Advanced Directive completed"
                required={false}
                name="advancedDirective"
                direction="row"
                options={yesNoOptions}
              />
              <FormRadioGroup
                disabled={readonly}
                label="POLST form completed"
                required={false}
                name="polstForm"
                direction="row"
                options={yesNoOptions}
              />
            </Stack>
          </Grid>

          <Grid item sm={12} xs={12}>
            <FormInput
              disabled={readonly}
              label="What matters"
              fullWidth
              name="whatMatters"
              autoComplete="new-password"
              helperText={
                <>
                  {WHAT_MATTERS_HELPER}
                  <br />
                  {whatMattersCharsLeft}
                </>
              }
              inputProps={{ maxLength: WHAT_MATTERS_MAX_LENGTH }}
            />
          </Grid>
        </Grid>

        {!readonly && (
          <Button
            variant="contained"
            type="submit"
            sx={{ minWidth: 160, height: 36.5, mt: 3, mb: 1, color: '#FFF' }}
          >
            {updater.isLoading && <CircularProgress size="1.5rem" color="inherit" />}
            {!updater.isLoading && 'Save'}
          </Button>
        )}
      </form>
    </FormProvider>
  )
}

export default ConcernProfileBasicInfoForm
